<template>
  <!-- 职员档案（本平台启用职员：登录权限 + 角色） -->
  <div class="staff-employee-list">
    <list-page>
      <search-condition @search="resetSearch" :show-search="true">
        <template #default>
          <el-input v-model="searchInfo.searchKey" placeholder="职员姓名/电话" @keydown.enter="resetSearch" clearable>
            <template #prefix>关键字</template>
          </el-input>
          <el-select v-model="searchInfo.roleId" placeholder="全部角色" clearable filterable>
            <template #prefix>角色</template>
            <el-option v-for="r in roleOptions" :key="r.id" :label="r.roleName" :value="r.id"/>
          </el-select>
        </template>
        <template #option>
          <page-button type="read" el-type="primary" @click="resetSearch">查询</page-button>
          <page-button type="write" el-type="success" @click="openAdd">+ 新建职员</page-button>
        </template>
      </search-condition>
      <list-table>
        <yq-table :data="dataList" height="100%" stripe border page-name="staffEmployeeList">
          <yq-table-column label="姓名" prop="employeeName" min-width="120rem">
            <template v-slot="{row}">
              <strong>{{ row.employeeName || '—' }}</strong>
            </template>
          </yq-table-column>
          <yq-table-column label="角色" prop="roleName" min-width="180rem">
            <template v-slot="{row}">
              <template v-if="row.roleList && row.roleList.length">
                <el-tag v-for="role in row.roleList" :key="role.id" size="small" style="margin-right: 0.4rem">
                  {{ role.roleName }}
                </el-tag>
              </template>
              <span v-else class="muted">—</span>
            </template>
          </yq-table-column>
          <yq-table-column label="联系电话" prop="employeePhone" min-width="140rem">
            <template v-slot="{row}">
              {{ row.employeePhone || '—' }}
            </template>
          </yq-table-column>
          <yq-table-column label="操作" prop="options" align="center" fixed="right" width="160rem" :show-overflow-tooltip="false">
            <template v-slot="{row}">
              <table-button @click="openDialog(row)">编辑</table-button>
              <table-button el-type="danger" message="是否确认将该职员从平台移除" @click="deleteRow(row.id)">移除</table-button>
            </template>
          </yq-table-column>
        </yq-table>
      </list-table>
      <form-page v-model:page-number="searchInfo.pageNumber" v-model:page-size="searchInfo.pageSize"
                 :total-count="searchInfo.totalCount" @onLoad="queryList"/>
    </list-page>

    <!-- 新增/编辑弹窗 -->
    <el-dialog v-model="showDialog" :title="dialogData.id ? '编辑职员档案' : '新建职员档案'" dialog-width="1" :close-on-click-modal="false">
      <form-items :data="dialogData" :columns="1" label-width="2.4rem" icon-width="0" ref="dialogForm">
        <form-item label="选择职员" :required="true" name="employeeId" error-message="请选择职员">
          <employee-select v-model="dialogData.employeeId" placeholder="请选择职员" :disabled="!!dialogData.id" @change="onEmployeeChange"/>
        </form-item>
        <form-item label="角色" :required="true" name="roleIdArr" error-message="请选择角色">
          <el-select v-model="dialogData.roleIdArr" placeholder="请选择角色" multiple filterable style="width: 100%">
            <el-option v-for="r in roleOptions" :key="r.id" :label="r.roleName" :value="r.id"/>
          </el-select>
        </form-item>
        <form-item label="联系电话">
          <el-input v-model="dialogData.employeePhone" disabled placeholder="来自职员档案"/>
        </form-item>
      </form-items>
      <template #footer>
        <page-button @click="showDialog = false">取消</page-button>
        <page-button type="write" el-type="primary" @click="saveData">保存</page-button>
      </template>
    </el-dialog>

    <!-- 新建：直接弹出多选职员，确认后批量开通登录权限 -->
    <employee-select-multi ref="employeeSelect" v-model="addEmployeeIds" hide-trigger @change="submitAdd"/>
  </div>
</template>

<script>
import ListPage from "@/components/h5/layout/ListPage.vue";
import SearchCondition from "@/components/h5/layout/SearchCondition.vue";
import ListTable from "@/components/h5/layout/ListTable.vue";
import FormPage from "@/components/h5/layout/FormPage.vue";
import TableButton from "@/components/h5/button/TableButton.vue";
import PageButton from "@/components/h5/button/PageButton.vue";
import YqTableColumn from "@/components/h5/table/YqTableColumn.vue";
import FormItems from "@/components/h5/layout/FormItems.vue";
import FormItem from "@/components/h5/layout/FormItem.vue";
import EmployeeSelect from "@/components/h5/data/EmployeeSelect.vue";
import EmployeeSelectMulti from "@/components/h5/data/EmployeeSelectMulti.vue";
import {useRoute, useRouter} from "vue-router";
import Util from "@/util/util";
import staffEmployeeApi from "@/api/dz-crm/sys/staffEmployeeApi";
import roleApi from "@/api/normal/sys/roleApi";

export default {
  setup() {
    return {router: useRouter(), route: useRoute()};
  },
  name: "StaffEmployeeList",
  components: {
    EmployeeSelect, EmployeeSelectMulti, FormItems, FormItem, YqTableColumn, TableButton, PageButton,
    SearchCondition, FormPage, ListTable, ListPage
  },
  data() {
    return {
      searchInfo: {pageNumber: 1},
      dataList: [],
      roleOptions: [],
      showDialog: false,
      dialogData: {},
      // 新建时多选职员的 id（逗号分隔，EmployeeSelectMulti 的 v-model）
      addEmployeeIds: "",
    };
  },
  beforeRouteLeave(to, from, next) {
    // 将当前页面数据进行缓存
    Util.cachePageData(this, to, from);
    next();
  },
  created() {
    if (!Util.loadPageData(this)) { // 加载缓存数据失败，初始化页面
      this.init();
      this.resetSearch();
    }
  },
  methods: {
    async init() {
      // 角色下拉（含列表筛选与表单角色）
      this.roleOptions = await roleApi.queryAllRoles() || [];
    },
    async queryList() {
      const {records, totalCount} = await staffEmployeeApi.queryList(this.searchInfo);
      this.dataList = records || [];
      this.searchInfo.totalCount = totalCount;
    },
    resetSearch() {
      this.searchInfo.pageNumber = 1;
      this.queryList();
    },
    // 新建：直接弹出职员多选
    openAdd() {
      this.addEmployeeIds = "";
      this.$refs.employeeSelect.open();
    },
    // 多选确认后批量开通登录权限
    async submitAdd(ids) {
      const employeeIds = String(ids || "").split(",").filter(id => id);
      if (!employeeIds.length) {
        return;
      }
      await staffEmployeeApi.saveBatch(employeeIds);
      this.addEmployeeIds = "";
      this.$message.success("已添加");
      this.resetSearch();
    },
    // 编辑：设置角色
    openDialog(row) {
      if (row) {
        this.dialogData = {
          id: row.id,
          employeeId: row.employeeId,
          employeeName: row.employeeName || "",
          employeePhone: row.employeePhone || "",
          roleIdArr: (row.roleIdArr || []).slice(),
        };
      } else {
        this.dialogData = {
          employeeId: "", employeeName: "", employeePhone: "", roleIdArr: [],
        };
      }
      this.showDialog = true;
    },
    // 选择职员：带出姓名/电话仅用于展示，登录权限与角色保存到 ba_merchant_employee / ba_employee_role
    onEmployeeChange(emp) {
      if (emp && emp.id) {
        this.dialogData.employeeName = emp.employeeName || "";
        this.dialogData.employeePhone = emp.employeePhone || "";
      }
    },
    async saveData() {
      if (!this.$refs.dialogForm.validCheck()) { // 数据验证不通过
        return;
      }
      if (!this.dialogData.roleIdArr || !this.dialogData.roleIdArr.length) {
        this.$message.warning("请选择角色");
        return;
      }
      await staffEmployeeApi.save({
        id: this.dialogData.id,
        employeeId: this.dialogData.employeeId,
        roleIdArr: this.dialogData.roleIdArr,
      });
      this.showDialog = false;
      this.resetSearch();
    },
    async deleteRow(id) {
      await staffEmployeeApi.delete(id);
      this.resetSearch();
    },
  },
};
</script>

<style lang="scss" scoped>
.staff-employee-list {
  height: 100%;
}
</style>
